import {ColorUtils} from "../utils/ColorUtils";
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit, ViewChild} from '@angular/core';
import {PlayerBaseService} from '../services/PlayerService/player.base.service';
import {IPlayer} from '../interfaces/IPlayer';
import {Log, LogTag} from '../services/Log';
import {forkJoin, mergeWith, Observable, of, Subject, switchMap} from 'rxjs';
import {MsgElm} from "./MsgElm";
import {PlayerAnim} from "./PlayerAnim";
import {map} from "rxjs/operators";



@Component({
  selector: 'app-player',
  template: '',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlayerBase implements OnInit, IPlayer {
    static defaultGpu = false;

    editMode = false;

    playerService: PlayerBaseService;

    changeDetectorRef: ChangeDetectorRef;
    hostView; // use to remove from parent


    @ViewChild('component', {static: true}) component;

    angle = 0.0;

    parentPlayer: IPlayer;
    providerData: any;
    fieldName: string;

    usePlayerLayoutX = true;
    usePlayerLayoutY = true;

    playing = false;

    playerObject: any;
    data: any;


    posX: number = 0;
    posY: number = 0;
    actualWidth: number;
    actualHeight: number;
    scaleX: number = 1.0;
    scaleY: number = 1.0;

    originalSize: any;

    playValue = 5000;

    msgElm: MsgElm;
    NO_PREVIEW = 'will display in deployed SignagePlayer<br>(preview coming soon)';
    PREVIEW = 'preview to play content';
    DEPLOYMENT = 'Content can be shown only in the Deployed SignPlayer';

    loadError = false;
    finish$: Subject<any>;

    animation: PlayerAnim;

    statEnabled = false;
    statTag;
    statFiled;
    minThreshold;
    maxThreshold;
    statStart;

    constructor(protected injector: Injector) {
        this.playerService = injector.get('playerService');
        this.changeDetectorRef = injector.get(ChangeDetectorRef);
        this.changeDetectorRef.detach();
        this.finish$ = new Subject<any>();
    }

    ngOnInit() {
    }

    init(parentPlayer: IPlayer, editMode: boolean, blockingTime: number) {
        this.parentPlayer = parentPlayer;
        this.editMode = editMode;

        if (this.editMode == false && blockingTime != -1) {
            setTimeout(() => {
                if (this.playerService.resellerId == 1) {
                    this.showMsg("Enterprise Component", "Requires subscription");
                } else {
                    this.showMsg("Service Interruption", "Contact admin");
                }

            }, blockingTime)
        }
    }


    load(playerObject: any): Observable<any> {
        const logInfo = Log.getLogInfo("PlayerBase", "load", []);

        // update from studio:
        if (this.playerObject != null) {
            this.clearEffects();
        }

        if (this.editMode) {
            this.playerObject = JSON.parse(JSON.stringify(playerObject));
        } else {
            this.playerObject = playerObject;
        }

        this.data = this.playerObject.Data[0];

        Log.d(logInfo, LogTag.PLAYER, this.constructor.name);

        this.loadPosAndSize();
        this.loadEffects();
        this.loadAnimation();


        if (this.component != null && this.data.Layout != null) {
            if (this.data.Layout[0].$.locked == "true") {
                this.component.nativeElement.classList.add("locked");
            } else {
                this.component.nativeElement.classList.remove("locked");
            }
            //this.component.nativeElement.style.visibility = (this.data.Layout[0].$.invisible == "true") ? "hidden" : "visible";
        }

        if (this.component != null) {
            this.component.nativeElement.style.overflow = "hidden";

            if (this.playerObject.$.clickEventName != null && this.playerObject.$.clickEventName != "") {
                this.component.nativeElement.style.cursor = "pointer";
            }
        }
        return of(this);
    }

    registerCommands() {
        if (this.editMode) {
            return of('commands');
        }

        const obsList = [];
        if (this.data.EventCommands != null && this.data.EventCommands[0] != null && this.data.EventCommands[0].EventCommand != null) {
            this.data.EventCommands[0].EventCommand.forEach((command) => {
                const from = command.$.from;
                const obs2$ = of('cmd').pipe(
                    switchMap(a => this.playerService.eventParams.getEventParam(from)),
                    switchMap(param => this.onCommand(command, param))
                )
                obsList.push(obs2$);
            });
        }

        if (obsList.length == 0) {
            return of('commands')
        }
        return forkJoin(obsList);
    }


    play(): Observable<any> {
        if (this.editMode) {
            this.display(true);
            return of('play');
        }
        const obsList = [];


        if (this.animation != null) {
            if (this.playerObject.$.clickEventName != null && this.playerObject.$.clickEventName != "") {
                this.animation.addTouchListeners();
            }
            obsList.push(this.animation.play());
        }


        if (this.animation != null) {
            const anim$ = this.animation.in();
            obsList.push(anim$);
        } else {
            this.display(true);
        }


        if (obsList.length == 0) {
            return of('play')
        }

        return forkJoin(obsList);
    }


    display(show) {
        if (this.editMode) {
            return;
        }
        this.component.nativeElement.style.display = show ? '' : 'none';
    }

    loadAnimation() {
        let animInfo = this.getAnimInfo();
        if (animInfo != null) {
            this.animation = new PlayerAnim(this, animInfo);
        }
    }

    getAnimInfo() {
        if (this.editMode) {
            return null;
        }

        if (this.data.Animation != null) {
            return this.data.Animation[0];
        } else if (this.parentPlayer != null) {
            return this.parentPlayer.getAnimInfo();
        } else {
            return null;
        }
    }

    unload(): Observable<any> {
        const self = this;
        if (this.animation != null) {
            return this.animation.out().pipe(
                map(a => self)
            );
        } else {
            return of(self);
        }
    }


    stop(): void {
        this.playing = false;
    }


    loadPosAndSize() {
        let parentLayout;
        if (this.parentPlayer != null && (this.parentPlayer instanceof PlayerBase) && this.playerObject.$.autoScaling == "1") {
            parentLayout = this.parentPlayer.data.Layout[0].$;
        }

        if (this.data.Layout != null && this.data.Layout[0].Actions != null) {
            let layout = this.data.Layout[0];
            this.actualWidth = Math.floor(Number(layout.$.width));
            this.actualHeight = Math.floor(Number(layout.$.height));
            let scaleX = 1;
            let scaleY = 1;
            if (parentLayout != null) {
                scaleX = this.parentPlayer.scaleX;
                scaleY = this.parentPlayer.scaleY;
            }
            if (this.component != null) {
                this.component.nativeElement.style.position = 'absolute';
            }
        } else {
            let layout = null;
            if (this.data.Layout != null) {
                layout = this.data.Layout[0];
            }

            if (this.component != null) {
                this.component.nativeElement.style.position = 'absolute';
            }

            if (layout != null && this.usePlayerLayoutX) {
                if (layout.$.x != null) {
                    if (parentLayout == null) {
                        this.posX = Math.floor(Number(layout.$.x));
                        if (this.component != null) {
                            this.component.nativeElement.style.left = this.posX + 'px';
                        }
                    } else {
                        this.posX = Math.floor(Number(layout.$.x) * this.parentPlayer.scaleX);
                        if (this.component != null) {
                            this.component.nativeElement.style.left = this.posX + 'px';
                        }
                    }
                }
                if (layout.$.left != null) {
                    this.posX = Math.floor(Number(layout.$.left));
                    if (this.component != null) {
                        this.component.nativeElement.style.left = this.posX + 'px';
                    }
                    if (layout.$.right) {
                        this.actualWidth = Math.floor(Number(this.parentPlayer.actualWidth) - Number(layout.$.left) - Number(layout.$.right));
                        if (this.component != null) {
                            this.component.nativeElement.style.width = this.actualWidth + 'px';
                        }
                    }
                } else if (layout.$.right) {
                    this.posX = Math.floor(Number(this.parentPlayer.actualWidth) - Number(layout.$.width) - Number(layout.$.right));
                    if (this.component != null) {
                        this.component.nativeElement.style.left = this.posX + 'px';
                    }
                }

                if (layout.$.horizontalCenter) {
                    this.posX = Math.floor(((Number(this.parentPlayer.actualWidth) - Number(layout.$.width)) * 0.5) + Number(layout.$.horizontalCenter));
                    if (this.component != null) {
                        this.component.nativeElement.style.left = this.posX + 'px';
                    }
                }

                if (layout.$.width != null) {
                    if (parentLayout == null) {
                        this.actualWidth = Math.floor(Number(layout.$.width));
                        if (this.component != null) {
                            this.component.nativeElement.style.width = this.actualWidth + 'px';
                        }
                    } else {
                        this.actualWidth = Math.floor(Number(layout.$.width) * this.parentPlayer.scaleX);
                        if (this.component != null) {
                            this.component.nativeElement.style.width = this.actualWidth + 'px';
                        }
                    }
                }
            } else {
                this.actualWidth = (this.parentPlayer != null) ? this.parentPlayer.actualWidth : window.innerWidth;


                if (this.component != null) {
                    this.component.nativeElement.style.width = this.actualWidth + 'px';
                }
            }


            if (layout != null && this.usePlayerLayoutY) {
                if (layout.$.y != null) {
                    if (parentLayout == null) {
                        this.posY = Math.floor(Number(layout.$.y));
                        if (this.component != null) {
                            this.component.nativeElement.style.top = this.posY + 'px';
                        }
                    } else {
                        this.posY = Math.floor(Number(layout.$.y) * this.parentPlayer.scaleY);
                        if (this.component != null) {
                            this.component.nativeElement.style.top = this.posY + 'px';
                        }
                    }
                }

                if (layout.$.top != null) {
                    this.posY = Math.floor(Number(layout.$.top));
                    if (this.component != null) {
                        this.component.nativeElement.style.top = this.posY + 'px';
                    }
                    if (layout.$.bottom) {
                        this.actualHeight = Math.floor(Number(this.parentPlayer.actualHeight) - Number(layout.$.top) - Number(layout.$.bottom))
                        if (this.component != null) {
                            this.component.nativeElement.style.height = this.actualHeight + 'px';
                        }
                    }
                } else if (layout.$.bottom) {
                    this.posY = Math.floor(Number(this.parentPlayer.actualHeight) - Number(layout.$.height) - Number(layout.$.bottom));
                    if (this.component != null) {
                        this.component.nativeElement.style.top = this.posY + 'px';
                    }
                }

                if (layout.$.verticalCenter) {
                    this.posY = Math.floor(((Number(this.parentPlayer.actualHeight) - Number(layout.$.height)) * 0.5) + Number(layout.$.verticalCenter));
                    if (this.component != null) {
                        this.component.nativeElement.style.top = this.posY + 'px';
                    }
                }


                if (layout.$.height != null) {
                    if (parentLayout == null) {
                        this.actualHeight = Math.floor(Number(layout.$.height))
                        if (this.component != null) {
                            this.component.nativeElement.style.height = this.actualHeight + 'px';
                        }
                    } else {
                        this.actualHeight = Math.floor(Number(layout.$.height) * this.parentPlayer.scaleY);
                        if (this.component != null) {
                            this.component.nativeElement.style.height = this.actualHeight + 'px';
                        }
                    }
                }
            } else {
                this.actualHeight = (this.parentPlayer != null) ? this.parentPlayer.actualHeight : window.innerHeight;
                if (this.component != null) {
                    this.component.nativeElement.style.height = this.actualHeight + 'px';
                }
            }

            if (layout != null && Number(layout.$.rotation) != this.angle) {
                this.angle = Math.floor(Number(layout.$.rotation));
                if (this.component != null) {
                    this.component.nativeElement.style.transform = "rotate(" + this.angle + "deg)";
                }
            }
        }
    }

    invalidateSize() {
    }


    createBackgroundAction() {
        const color = Number(this.data.Background[0].$.color);
        const r = color & 0xff;
        const g = (color>>8) & 0xff;
        const b = (color>>16) & 0xff;
        const alpha = Number(this.data.Background[0].$.alpha);
        this.component.nativeElement.style.backgroundColor = 'rgba(' + b + ',' + g + ',' + r + ',' +  alpha + ')';
    }

    clearEffects() {
        this.component.nativeElement.style.backgroundColor = '';
        this.component.nativeElement.style.border = '';
        this.component.nativeElement.style.opacity = '';
        this.component.nativeElement.style.filter = '';
    }

    loadEffects() {
        if (this.component == null) {
            return;
        }

        if (this.data.Background != null) {
            this.createBackgroundAction();
        }

        if (this.data.Border != null) {
            const borderColor = ColorUtils.format(Number(this.data.Border[0].$.borderColor));
            const borderThickness = this.data.Border[0].$.borderThickness;
            this.component.nativeElement.style.border = "solid";
            this.component.nativeElement.style.borderColor = '#' + borderColor;
            this.component.nativeElement.style.borderWidth = borderThickness + 'px';
        }

        if (this.data.Appearance != null) {
            this.component.nativeElement.style.opacity = this.data.Appearance[0].$.alpha;
        }

        if (this.data.Glow != null) {
            const color = Number(this.data.Glow[0].$.color);
            const r = color & 0xff;
            const g = (color>>8) & 0xff;
            const b = (color>>16) & 0xff;
            const blur = this.data.Glow[0].$.blur;
            const alpha = this.data.Glow[0].$.alpha;
            this.component.nativeElement.style.filter = 'drop-shadow(0px 0px '+ blur +'px rgba(' + b + ',' + g + ',' + r + ',' +  alpha + '))';
        }

        if (this.data.Blur != null) {
            const blurX = this.data.Blur[0].$.blurX;
            this.component.nativeElement.style.filter = 'blur(' + blurX + 'px)';
        }

        if (this.data.Shadow != null) {
            const alpha =  +(this.data.Shadow[0].$.alpha);
            const rotation =  +(this.data.Shadow[0].$.angle);
            const blur  = +(this.data.Shadow[0].$.blur);
            const color  = Number(this.data.Shadow[0].$.color);
            const distance = +(this.data.Shadow[0].$.distance);

            const r = color & 0xff;
            const g = (color>>8) & 0xff;
            const b = (color>>16) & 0xff;
            const k = 0.01745329252;
            const hShadow = distance * Math.cos(rotation * k);
            const vShadow = distance * Math.sin(rotation * k);
            this.component.nativeElement.style.filter = 'drop-shadow(' + hShadow + 'px ' + vShadow + 'px '+ blur +'px rgba(' + b + ',' + g + ',' + r + ',' +  alpha + '))';
        }
    }


    loadStat() {
        if (this.data.Stat != null) {
            const stat = this.data.Stat[0].$;
            this.statEnabled = (stat.enabled == '1');
            this.statTag = stat.tag;
            this.statFiled = stat.field;

            if (stat.minThreshold != null && stat.maxThreshold != null) {
                this.minThreshold = Number(stat.minThreshold);
                this.maxThreshold = Number(stat.maxThreshold);
            }
        }
    }


    getFontStyle(font) {
        let fontSize: number = Number(font.fontSize);
        if (font.autoScaling == '1') {
            fontSize *= Math.min(this.parentPlayer.scaleX, this.parentPlayer.scaleY);
        }


        const color = '#' + ColorUtils.format(Number(font.fontColor));
        let fontFamily = font.fontFamily;
        if (font.fontWeight != "Normal" && font.fontWeight != "none" && font.fontWeight != "normal" && font.fontWeight != "bold") {
            fontFamily += (font.fontWeight == "bold") ? "Bold" : font.fontWeight;
        }
        if (font.fontStyle != "Normal" && font.fontStyle != "normal" && font.fontStyle != "italic") {
            fontFamily += (font.fontStyle == "italic") ? "Italic" : font.fontStyle;
        }

        fontFamily = fontFamily.replace(/[\s\[\]\-!•™&:.,()`'?]/g, '');
        const wrap = (font.wrap == "1");

        return {
            fontFamily: fontFamily,
            fontSize: fontSize,
            fill: color,
            textDecoration: font.textDecoration,
            wrap: wrap,
            wordWrapWidth: this.actualWidth,
            align: font.textAlign,
            alignSelf: font.alignSelf
        };
    }

    onClick(event) {
        const logInfo = Log.getLogInfo("PlayerBase", "onClick", [event]);
        Log.d(logInfo, LogTag.PLAYER, 'SendEvent ' + this.playerObject.$.clickEventName + " " + this.playerObject.$.clickEventParam);
        const clickEventName = this.playerObject.$.clickEventName;
        const clickEventParam = this.playerObject.$.clickEventParam;

        let param = ""
        try {
            param = JSON.parse(clickEventParam);
        } catch (e) {
            param = clickEventParam;
        }

        this.playerService.eventParams.dispatch(clickEventName, param, false);

    }

    getGlobalPos(): any {
        debugger;
        console.log('getGlobalPos not supported')
        return null;
    }


    onCommand(command, param): Observable<any> {
        return of(null);
    }


    showMsg(title, description) {
        if (title != null) {
            if (this.msgElm == null) {
                this.msgElm = new MsgElm(this.component);
            }
            this.msgElm.setText(title, description, this.actualWidth, this.actualHeight);
        } else {
            if (this.msgElm != null) {
                this.msgElm.destroy();
                this.msgElm = null;
            }
        }
    }

    addContent(statTag, statField, duration) {

    }

    logError(err): Observable<any> {
        Log.debug();

        const logInfo = Log.getLogInfo("PlayerBase", "logError", []);
        this.loadError = true;
        let errMsg = "";
        if (err.constructor.name == 'TypeError') {
            errMsg = (<TypeError>err).message;
        } else {
            try {
                errMsg = JSON.stringify(err)
            } catch (e) {
                errMsg = err;
            }
        }
        Log.e(logInfo, LogTag.PLAYER, errMsg);
        this.showMsg(this.constructor.name, "Error: " + err);
        return of(null);
    }

    destroy(): void {
        const logInfo = Log.getLogInfo("PlayerBase", "destroy", []);
        Log.d(logInfo, LogTag.PLAYER, this.constructor.name);

        if (this.finish$ != null) {
            this.finish$.next("finish");
            this.finish$.complete();
            this.finish$ = null;
        }

        if (this.msgElm != null) {
            this.msgElm.destroy();
            this.msgElm = null;
        }

        if (this.animation != null) {
            this.animation.removeTouchListeners();
            this.animation = null;
        }


        this.playerService = null;
        this.changeDetectorRef = null;
        this.playerObject = null;
        this.data = null;
    }
}
